<template>
  <SvgSprite />
  <div class="as-content-page as-match-detail">
    <TabsIcon
      :tabs="tabs"
      :activeTab="activeTab"
      :isMin="isChatTyping"
      @change="changeTab"
    />

    <div
      id="as-match-detail-content"
      class="as-match-detail-content"
      :class="{
        'as-hide-content': isHideContent,
      }"
      :style="{
        height: `${heightContent}px`,
      }"
    >
      <div v-show="activeTab === 'chat'">
        <IframeChat />
      </div>
      <div v-if="activeTab === 'statistics'">
        <StatisticsList
          v-if="tabLoading.statistics"
          :match="match"
          :statistics="statistics"
        />
        <H2hListSkeleton v-else />
      </div>
      <div v-else-if="activeTab === 'overview'">
        <DetailPointList
          v-if="tabLoading.overview"
          :match="match"
          :overviews="overviews"
        />
        <H2hListSkeleton v-else />
      </div>
      <div v-else-if="activeTab === 'bracket'">
        <BracketTabList
          v-if="tabLoading.bracket"
          :match="match"
          :tournament="tournament"
        />
        <H2hListSkeleton v-else />
      </div>
      <div v-else-if="activeTab === 'h2h'">
        <H2hList
          v-if="tabLoading.h2h || tabLoading.next_match || isObjectNotEmpty(h2h)"
          :match="match"
          :h2h="h2h"
        />
        <H2hListSkeleton v-else />
      </div>
      <div
        v-else-if="activeTab === 'match_other'"
        :class="`as-parent-h2h as-parent-h2h__item-h-auto as-parent-h2h-${siteName}`"
      >
        <MatchOther
          v-if="tabLoading.match_other"
          :matchLiveData="matchOtherData"
          :matchId="matchId"
          mode="match-other"
        />
        <H2hListSkeleton v-else />
      </div>
      <div
        v-else-if="activeTab === 'next_match'"
        :class="`as-parent-h2h as-parent-h2h__item-h-auto as-parent-h2h-${siteName}`"
      >
        <NextMatchList
          v-if="tabLoading.h2h || tabLoading.next_match || isObjectNotEmpty(h2h)"
          :match="match"
          :h2h="h2h"
        />
        <H2hListSkeleton v-else />
      </div>
      <div v-else-if="activeTab === 'odd'">
        <OddTab :sportType="sportType" :odd="odd" :oddOld="oddOld" />
      </div>
      <div v-else-if="activeTab === 'information'">
        <Information :match="match" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import H2hListSkeleton from "~src/components/skeleton/result-popup/H2hListSkeleton.vue";
import {
  matchStatistics,
  matchH2h,
  matchStanding,
  matchLineups,
  matchDetail,
  matchDetailLive,
  shootingPointsMatch,
  seasonBracketMatch,
  getOddsMatch,
} from "~src/services/SpapiService";
import { getMatchLive } from "~src/services/RemoteLiveService";
import {
  addMatchDetailLive,
  convertData,
  initializeData,
  convertShootingPointsData,
  groupByPlayer,
  mapTeamToH2H,
} from "~src/helpers/tennis";
import { convertLineupsData } from "~src/helpers/basketball/LineupHelper";
import type {
  IMatch,
  IMatchMapping,
  IMatchLiveData,
  IOddMatch,
  IItemString,
  IStatsJsonData,
  ITournamentJsonData,
  IMatchTennis,
  IStatisticsJsonData,
  IH2h,
  ITeam,
} from "~src/interfaces/tennis";
import IncidentsList from "~src/components/incidents/tennis/IncidentsList.vue";
import DetailPointList from "~src/components/points/tennis/DetailPointList.vue";
import StatisticsList from "~src/components/statistics/tennis/StatisticsList.vue";
import H2hList from "~src/components/h2h/tennis/H2hList.vue";
import Information from "~src/components/matches/tennis/Information.vue";
import NextMatchList from "~src/components/matches/tennis/NextMatchList.vue";
import MatchOther from "~src/components/matches/tennis/MatchOther.vue";
import OddTab from "~src/components/odds/OddTab.vue";
import IframeChat from "~src/components/IframeChat.vue";
import IconStats from "~src/components/icons/menus/IconStats.vue";
import IconChat from "~src/components/icons/menus/IconChat.vue";
import IconH2h from "~src/components/icons/menus/IconH2h.vue";
import IconMatchOther from "~src/components/icons/menus/IconMatchOther.vue";
import IconOdd from "~src/components/icons/menus/IconOdd.vue";
import IconInfo from "~src/components/icons/menus/IconInfo.vue";
import SvgSprite from "~src/components/matches/SvgSprite.vue";
import MatchEnum from "~src/enums/tennis/MatchEnum";
import Tennis from "~src/components/icons/Tennis.vue";
import IconSchematic from "~src/components/icons/menus/tennis/IconSchematic.vue";
import SchematicListTab from "~src/components/schematic/tennis/SchematicListTab.vue";
import BracketTabList from "~src/components/bracket/tennis/BracketTabList.vue";
import TabsIcon from "~src/components/TabsIcon.vue";
import IconLive from "~src/components/icons/menus/IconLive.vue";
import { useMatchDetailStore } from "~src/stores/MatchDetailStore";
import { isObjectNotEmpty } from "~src/helpers";
import { EVENT_ODDS_LIVE_DATA_UPDATED } from "~src/utils/constant";

export default defineComponent({
  name: "matchDetail",
  components: {
    H2hListSkeleton,
    IncidentsList,
    StatisticsList,
    H2hList,
    NextMatchList,
    OddTab,
    Information,
    IframeChat,
    SvgSprite,
    SchematicListTab,
    BracketTabList,
    DetailPointList,
    TabsIcon,
    MatchOther,
  },
  props: {
    matchId: {
      type: String,
    },
    apiLiveUrl: {
      type: String,
    },
    isFetchOdd: {
      type: Boolean
    }
  },
  data() {
    const matchDetailStore = useMatchDetailStore();

    return {
      matchDetailStore,
      match: {} as IMatchTennis,
      matchMapping: {} as IMatchMapping,
      incidents: [],
      statistics: [] as IStatsJsonData[] | any,
      overviews: [] as IStatisticsJsonData[] | any,
      h2h: {} as IH2h,
      standing: {},
      lineups: {},
      tournament: {} as ITournamentJsonData | {},
      shootingPointData: null as any,
      detailShooting: null as any,
      data: null,
      odd: [] as IOddMatch[],
      oddOld: [] as IOddMatch[],
      matchOtherData: {} as IMatchLiveData,
      matchOthers: [] as Array<IMatch>,
      isLoading: false,
      isLoadingInterval: false,
      isShowChat: true,
      tabs: [
        {
          key: "statistics",
          title: "Thông số",
          icon: IconStats,
        },
        {
          key: "overview",
          title: "Điểm chi tiết",
          icon: Tennis,
        },
        {
          key: "bracket",
          title: "Sơ đồ",
          icon: IconSchematic,
        },
        {
          key: "chat",
          title: "Chat",
          icon: IconChat,
        },
        {
          key: "h2h",
          title: "Đối đầu",
          icon: IconH2h,
        },
        {
          key: "match_other",
          title: "Trận khác",
          icon: IconLive,
        },
        {
          key: "next_match",
          title: "Trận sắp tới",
          icon: IconMatchOther,
        },
        {
          key: "odd",
          title: "Tỷ lệ kèo",
          icon: IconOdd,
        },
        {
          key: "information",
          title: "TT trận",
          icon: IconInfo,
        },
      ],
      tabLoading: {} as IItemString,
      timers: [] as NodeJS.Timeout[]
    };
  },
  computed: {
    activeTab(): string {
      return this.matchDetailStore.getActiveTab;
    },
    isChatTyping(): boolean {
      return this.matchDetailStore.getIsChatTyping;
    },
    isHideContent(): boolean {
      return this.matchDetailStore.getIsHideContent;
    },
    heightContent(): number {
      return this.matchDetailStore.getHeightContent;
    },
    sportType() {
      // @ts-ignore
      return this.gSportType;
    },
    siteName() {
      // @ts-ignore
      return this.gSiteName;
    },
  },
  async created() {
    const [rsMatch] = await Promise.all([
      matchDetail(this.matchId, this.sportType),
    ]);

    this.match =
      rsMatch.data.data && rsMatch.data.data.data
        ? rsMatch.data.data.data
        : rsMatch.data.data;

    await this.changeTab("chat");
  },
  async mounted() {
    const element = this.$refs.asMatchDetail as HTMLElement;
    if (element) {
      // @ts-ignore
      this.matchDetailStore.setScreenWidth(
        element?.getBoundingClientRect().width
      );
    }

    const interval1 = setInterval(async () => {
      if (
        this.match.status_id !== MatchEnum.STATUS_END &&
        !this.isLoadingInterval &&
        this.activeTab !== "odd"
      ) {
        this.isLoadingInterval = true;
        await this.changeTab(this.activeTab, true);
        this.isLoadingInterval = false;
      }
    }, 21000);
    this.timers.push(interval1);

    if (this.isFetchOdd) {
      const interval2 = setInterval(async () => {
        if (
          this.match.status_id !== MatchEnum.STATUS_END &&
          !this.isLoadingInterval &&
          this.activeTab === "odd"
        ) {
          this.isLoadingInterval = true;
          await this.changeTab(this.activeTab, true);
          this.isLoadingInterval = false;
        }
      }, 5000);
      this.timers.push(interval2);
    } else {
      window.addEventListener(EVENT_ODDS_LIVE_DATA_UPDATED, this.handleOddsLiveDataUpdated);
    }
  },
  async beforeUnmount() {
    this.timers.forEach(clearInterval);

    if (!this.isFetchOdd) {
      window.removeEventListener(EVENT_ODDS_LIVE_DATA_UPDATED, this.handleOddsLiveDataUpdated);
    }
  },
  methods: {
    async changeTab(tabName: string, isInterval = false) {
      this.matchDetailStore.setActiveTab(tabName);
      const matchId = this.matchId;
      const seasonId = this.match?.season_id ?? "";
      this.isLoading = true;

      if (tabName === "statistics") {
        try {
          const { data: { data: statistics = [] }} = await matchStatistics(matchId, this.sportType);
          const parseData = await convertData(statistics, matchId as string);
          this.statistics = parseData || [initializeData()];
        } catch (e) {
          console.log("Load statistics error", e);
        }
      } else if (tabName === "overview") {
        try {
          const { data: { data: overviews = [] }} = await matchStatistics(matchId, this.sportType);
          this.overviews = overviews;
        } catch (e) {
          console.log("Load overview error", e);
        }
      } else if (tabName === "h2h" || tabName === "next_match") {
        try {
          const { data: { data: h2h = [] }} = await matchH2h(matchId, this.sportType);
          const __h2h = mapTeamToH2H(h2h);
          this.h2h = __h2h;
        } catch (e) {
          console.log("Load h2h error", e);
        }
      } else if (tabName === "match_other") {
        try {
          let [rsMatch, rsLive] = await Promise.all([
            getMatchLive(this.apiLiveUrl, this.sportType, this.siteName),
            matchDetailLive(this.sportType),
          ]);

          const teams = rsMatch.data.teams ?? [];
          const matches = await addMatchDetailLive(
            rsMatch.data.matches,
            rsLive.data.results,
            teams
          );

          this.matchOtherData = {
            competitions: rsMatch.data.competitions,
            teams: teams,
            commentators: rsMatch.data.commentators,
            matches,
          } as IMatchLiveData;
        } catch (e) {
          console.log("Load match other error", e);
        }
      } else if (tabName === "standing") {
        if (seasonId) {
          try {
            const { data: { data: standing = {} }} = await matchStanding(seasonId, this.sportType);
            this.standing = standing;
          } catch (e) {
            console.log("Load standing error", e);
          }
        }
      } else if (tabName === "lineups") {
        try {
          const { data: { data: lineups = {} }} = await matchLineups(matchId, this.sportType);
          this.lineups = lineups
            ? convertLineupsData(lineups)
            : {};
        } catch (e) {
          console.log("Load lineups error", e);
        }
      } else if (tabName === "odd") {
        try {
          const { data: { data: odd = [] }} = await getOddsMatch(this.sportType, this.matchId);
          this.oddOld = JSON.parse(JSON.stringify(this.odd));
          this.odd = odd && odd.length ? odd : [];
        } catch (e) {
          console.log("Load odd error", e);
        }
      } else if (tabName === "shooting") {
        try {
          const { data: { shootingPoints = {} }} = await shootingPointsMatch(matchId, this.sportType);
          const lineupsRealtimeData = await matchLineups(
            matchId,
            this.sportType
          );
          const shootingPointsConverted = convertShootingPointsData(shootingPoints);
          const groupByPlayerData = groupByPlayer(shootingPoints?.shootingPoints);
          this.shootingPointData = {
            shootingPointsConverted,
            match: this.match,
            listPlayer: shootingPoints?.listPlayer,
            shootingPoints: shootingPoints?.shootingPoints,
            groupByPlayerData,
            lineup: lineupsRealtimeData?.data?.data,
          };
        } catch (e) {
          console.log("Load shooting error", e);
        }
      } else if (tabName === "bracket") {
        try {
          const { data: { data: tournament = {} }} = await seasonBracketMatch(seasonId, this.sportType);
          this.tournament = tournament;
        } catch (e) {
          console.log("Load bracket error", e);
        }
      }

      this.isLoading = false;
      this.tabLoading[tabName] = true;
    },
    isObjectNotEmpty,
    handleOddsLiveDataUpdated(e: Event) {
      const oddsData = (e as CustomEvent<IOddMatch[]>).detail;
      if (oddsData && this.activeTab === "odd") {
        this.oddOld = JSON.parse(JSON.stringify(this.odd));
        this.odd = oddsData;
      }
    },
  },
});
</script>

<style scoped></style>
